/**
 * AuthorityChainsTab — Trace any exception type from detection to authoritative standard
 * Structured vertical flow with boxes for each layer
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, safeFetch } from '../../constants';

const KE = `${API_V1}/knowledge-engine`;

const filterBtn = (active: boolean): React.CSSProperties => ({
  padding: '4px 12px', borderRadius: 4, fontSize: 11, cursor: 'pointer', border: 'none',
  background: active ? 'var(--accent)' : 'var(--bg-elevated)',
  color: active ? '#fff' : 'var(--text-secondary)',
  transition: 'all 0.15s',
});

const chainBox = (borderColor: string, bgTint: string): React.CSSProperties => ({
  padding: '14px 18px', borderRadius: 8, border: `1.5px solid ${borderColor}`,
  background: bgTint, position: 'relative' as const,
});
const chainLabel: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const,
  letterSpacing: 0.8, marginBottom: 8, color: 'var(--text-tertiary)',
};
const arrow: React.CSSProperties = {
  textAlign: 'center' as const, padding: '4px 0', fontSize: 11,
  color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums',
};
const mono: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums', fontSize: 12,
};
const detailRow: React.CSSProperties = {
  fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7,
};

export default function AuthorityChainsTab() {
  const [types, setTypes] = useState<string[]>([]);
  const [selected, setSelected] = useState('');
  const [fw, setFw] = useState<'US_GAAP' | 'IFRS'>('US_GAAP');
  const [chain, setChain] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Load exception types from coverage report
  useEffect(() => {
    safeFetch(`${KE}/coverage-report`, {}).then((d: any) => {
      if (!d || typeof d !== 'object') { setTypes([]); return; }
      const all: string[] = [];
      if (d.covered_types) {
        (Array.isArray(d.covered_types) ? d.covered_types : []).forEach((t: any) => {
          all.push(typeof t === 'string' ? t : (t?.exception_type || t?.type || ''));
        });
      }
      if (d.gaps) {
        (Array.isArray(d.gaps) ? d.gaps : []).forEach((t: any) => {
          all.push(typeof t === 'string' ? t : (t?.exception_type || t?.type || ''));
        });
      }
      // fallback: try types array
      if (all.length === 0 && d.types) {
        (Array.isArray(d.types) ? d.types : []).forEach((t: any) => {
          all.push(typeof t === 'string' ? t : (t?.exception_type || t?.type || ''));
        });
      }
      const sorted = [...new Set(all.filter(Boolean))].sort();
      setTypes(sorted);
      if (sorted.length > 0 && !selected) setSelected(sorted[0]);
    });
  }, []); // eslint-disable-line

  // Load chain
  const loadChain = useCallback(() => {
    if (!selected) return;
    setLoading(true);
    authFetch(`${KE}/authority-chain/${encodeURIComponent(selected)}?framework=${fw}`)
      .then(r => r.ok ? r.json() : null)
      .then(setChain)
      .catch(() => setChain(null))
      .finally(() => setLoading(false));
  }, [selected, fw]);

  useEffect(() => { loadChain(); }, [loadChain]);

  const pattern = chain?.pattern || chain?.resolution_pattern;
  const jeTemplate = pattern?.je_template || chain?.je_template;
  const xbrlConcepts = chain?.xbrl_concepts || [];
  const standard = chain?.standard || chain?.reference_document;
  const controls = chain?.controls || chain?.control_objective;
  const failure = chain?.failure_pattern;
  const detection = chain?.detection_rule || chain?.detection;

  return (
    <div>
      {/* Select + Framework */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={selected}
          onChange={e => setSelected(e.target.value)}
          style={{
            padding: '8px 12px', fontSize: 12, borderRadius: 6, minWidth: 280,
            border: '1px solid var(--border-subtle)', background: 'var(--bg-elevated)',
            color: 'var(--text-primary)', outline: 'none',
          }}
        >
          <option value="">Select exception type...</option>
          {types.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setFw('US_GAAP')} style={filterBtn(fw === 'US_GAAP')}>US GAAP</button>
          <button onClick={() => setFw('IFRS')} style={filterBtn(fw === 'IFRS')}>IFRS</button>
        </div>
        {loading && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Loading chain...</span>}
      </div>

      {!selected && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
          Select an exception type to view its authority chain.
        </div>
      )}

      {selected && !loading && !chain && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
          No authority chain found for "{selected.replace(/_/g, ' ')}".
        </div>
      )}

      {chain && !loading && (
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          {/* Detection Rule */}
          {detection && (
            <>
              <div style={chainBox('#FACC15', '#FACC1508')}>
                <div style={chainLabel}>Detection</div>
                <div style={detailRow}>
                  <strong style={{ color: 'var(--text-primary)' }}>
                    {detection.rule_name || detection.name || 'Detection Rule'}
                  </strong><br />
                  {detection.category && <>Category: {detection.category}<br /></>}
                  {detection.severity && <>Severity: {detection.severity}<br /></>}
                </div>
              </div>
              <div style={arrow}>↓ detects</div>
            </>
          )}

          {/* Exception Type */}
          <div style={chainBox('#22D3EE', '#22D3EE08')}>
            <div style={chainLabel}>Exception Type</div>
            <div style={detailRow}>
              <strong style={{ color: 'var(--text-primary)', fontSize: 14 }}>
                {selected.replace(/_/g, ' ')}
              </strong><br />
              Framework: {fw}
              {chain.exception_count != null && <> · {chain.exception_count} in database</>}
            </div>
          </div>
          <div style={arrow}>↓ resolved_by</div>

          {/* Resolution Pattern */}
          {pattern ? (
            <div style={chainBox('#4ADE80', '#4ADE8008')}>
              <div style={chainLabel}>Resolution Pattern</div>
              <div style={detailRow}>
                <strong style={{ color: 'var(--text-primary)' }}>
                  {pattern.pattern_name || pattern.name} {pattern.id && `(#${pattern.id})`}
                </strong><br />
                {pattern.times_used != null && (
                  <>Quality: {pattern.times_used} used, {pattern.times_accepted ?? 0} accepted
                    {(pattern.times_used ?? 0) > 0 && ` (${Math.round(((pattern.times_accepted ?? 0) / (pattern.times_used ?? 1)) * 100)}%)`}
                    <br />
                  </>
                )}
              </div>
              {/* JE Template */}
              {jeTemplate?.lines && (
                <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(74,222,128,0.06)', borderRadius: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 4 }}>JE TEMPLATE</div>
                  {jeTemplate.lines.map((l: any, i: number) => (
                    <div key={i} style={{ ...mono, marginBottom: 2 }}>
                      <span style={{ color: l.direction === 'debit' ? '#60A5FA' : '#FB923C', fontWeight: 700, width: 28, display: 'inline-block' }}>
                        {l.direction === 'debit' ? 'DR' : 'CR'}
                      </span>
                      <span style={{ color: 'var(--text-primary)' }}>{l.xbrl_concept || 'N/A'}</span>
                      <span style={{ color: 'var(--text-tertiary)', marginLeft: 8 }}>({l.account_type})</span>
                      <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>${l.amount_formula}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ ...chainBox('#F87171', '#F8717108'), opacity: 0.7 }}>
              <div style={chainLabel}>Resolution Pattern</div>
              <div style={{ color: '#F87171', fontSize: 12 }}>No pattern found — relies on hardcoded resolver or Mode 2 (AI)</div>
            </div>
          )}

          {/* XBRL Concepts */}
          {xbrlConcepts.length > 0 && (
            <>
              <div style={arrow}>↓ debit_account / credit_account</div>
              <div style={{ display: 'flex', gap: 12 }}>
                {xbrlConcepts.map((c: any, i: number) => (
                  <div key={i} style={{ ...chainBox('#60A5FA', '#60A5FA08'), flex: 1 }}>
                    <div style={chainLabel}>XBRL Concept</div>
                    <div style={detailRow}>
                      <strong style={{ color: '#60A5FA', ...mono, fontSize: 11 }}>{c.element_name || c.concept}</strong><br />
                      {c.statement && <>{c.statement}, {c.account_type}, {c.normal_balance}<br /></>}
                      {(c.asc_paragraph || c.ifrs_reference || c.reference) && (
                        <span style={{ ...mono, fontSize: 11, color: 'var(--text-tertiary)' }}>
                          {c.asc_paragraph || c.ifrs_reference || c.reference}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Standard */}
          {standard && (
            <>
              <div style={arrow}>↓ cites_standard</div>
              <div style={chainBox('#94A3B8', '#94A3B808')}>
                <div style={chainLabel}>Authoritative Standard</div>
                <div style={detailRow}>
                  <strong style={{ color: 'var(--text-primary)' }}>
                    {standard.document_code || standard.code} — {standard.title || standard.name}
                  </strong><br />
                  {standard.issuing_body && <>Issuing Body: {standard.issuing_body}<br /></>}
                  {standard.framework && <>Framework: {standard.framework}<br /></>}
                  {(chain.section_reference || standard.section_reference) && (
                    <span style={{ ...mono, fontSize: 11 }}>Section: {chain.section_reference || standard.section_reference}</span>
                  )}
                </div>
                {chain.citation_text && (
                  <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(148,163,184,0.06)', borderRadius: 6, fontStyle: 'italic', fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    "{chain.citation_text}"
                  </div>
                )}
              </div>
            </>
          )}

          {/* Controls + Failure */}
          {(controls || failure) && (
            <>
              <div style={arrow}>↓ violates / indicates_failure</div>
              <div style={chainBox('#A78BFA', '#A78BFA08')}>
                <div style={chainLabel}>Controls</div>
                <div style={detailRow}>
                  {controls && (
                    <>
                      <strong style={{ color: 'var(--text-primary)' }}>
                        {controls.objective_name || controls.name || 'Control Objective'}
                      </strong><br />
                      {controls.cycle_name && <>Cycle: {controls.cycle_name}<br /></>}
                    </>
                  )}
                  {failure && (
                    <>
                      Failure Pattern: {failure.pattern_name || failure.name}<br />
                      {failure.risk_category && <>Risk: {failure.risk_category}<br /></>}
                      {failure.root_cause && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Root cause: {failure.root_cause}</span>}
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Divergence comparison */}
          {chain.divergence && (
            <>
              <div style={{ marginTop: 16, padding: 14, background: '#FB923C0A', border: '1px solid #FB923C40', borderRadius: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#FB923C', marginBottom: 6 }}>
                  ⚠ FRAMEWORK DIVERGENCE
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <div style={{ flex: 1, padding: 10, background: 'rgba(37,99,235,0.06)', borderRadius: 6, fontSize: 12 }}>
                    <div style={{ fontWeight: 600, color: '#93C5FD', marginBottom: 4 }}>US GAAP</div>
                    <div style={{ color: 'var(--text-secondary)' }}>{chain.divergence.us_gaap_treatment || chain.divergence.us_gaap || '--'}</div>
                  </div>
                  <div style={{ flex: 1, padding: 10, background: 'rgba(74,222,128,0.06)', borderRadius: 6, fontSize: 12 }}>
                    <div style={{ fontWeight: 600, color: '#4ADE80', marginBottom: 4 }}>IFRS</div>
                    <div style={{ color: 'var(--text-secondary)' }}>{chain.divergence.ifrs_treatment || chain.divergence.ifrs || '--'}</div>
                  </div>
                </div>
                {chain.divergence.notes && (
                  <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-tertiary)' }}>{chain.divergence.notes}</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
